import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  Box, TextField, Button, Typography, Paper, CircularProgress, Alert,
  MenuItem, Select, InputLabel, FormControl, Grid, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Chip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { formatDateOnly } from '../utils/dateTime';
import { filterCardSx } from '../styles/uiTokens';

const TIPO_MANUTENCAO_LABELS = {
  corretiva: 'Corretiva',
  preventiva: 'Preventiva',
  recorrente: 'Recorrente',
};

function ManutencaoRelatorio() {
  const [locais, setLocais] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [equipamentos, setEquipamentos] = useState([]);

  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [localId, setLocalId] = useState('');
  const [tipoId, setTipoId] = useState('');
  const [equipamentoId, setEquipamentoId] = useState('');

  const [executadas, setExecutadas] = useState(null);
  const [aExecutar, setAExecutar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPdf, setLoadingPdf] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/manutencao-locais'),
      api.get('/tipos-equipamento'),
      api.get('/equipamentos?lista_leve=true'),
    ])
      .then(([localRes, tipoRes, eqRes]) => {
        setLocais(localRes.data || []);
        setTipos(tipoRes.data || []);
        setEquipamentos(eqRes.data || []);
      })
      .catch((err) => console.error('Erro ao carregar filtros do relatório:', err));
  }, []);

  const buildParams = () => {
    const params = { data_inicio: dataInicio, data_fim: dataFim };
    if (localId) params.local_id = localId;
    if (tipoId) params.tipo_equipamento_id = tipoId;
    if (equipamentoId) params.equipamento_id = equipamentoId;
    return params;
  };

  const handleBuscar = () => {
    if (!dataInicio || !dataFim) {
      setError('Informe o período (data inicial e final).');
      return;
    }
    setError('');
    setLoading(true);
    const params = buildParams();

    Promise.all([
      api.get('/manutencoes/relatorio-executadas', { params }),
      api.get('/manutencoes/relatorio-a-executar', { params }),
    ])
      .then(([execRes, aExecRes]) => {
        setExecutadas(execRes.data || []);
        setAExecutar(aExecRes.data || []);
      })
      .catch((err) => {
        console.error('Erro ao gerar relatório:', err);
        setError('Erro ao gerar o relatório.');
      })
      .finally(() => setLoading(false));
  };

  const handlePdf = (tipo) => {
    if (!dataInicio || !dataFim) return;
    setLoadingPdf(tipo);
    const params = buildParams();
    const url = tipo === 'executadas' ? '/manutencoes/relatorio-executadas-pdf' : '/manutencoes/relatorio-a-executar-pdf';

    api.get(url, { params, responseType: 'blob' })
      .then((response) => {
        const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        window.open(blobUrl, '_blank');
      })
      .catch((err) => {
        console.error('Erro ao gerar PDF:', err);
        setError('Erro ao gerar o PDF.');
      })
      .finally(() => setLoadingPdf(null));
  };

  const renderStatus = (status) => (
    status === 'atrasada'
      ? <Chip label="Atrasada" color="error" size="small" />
      : <Chip label="Em dia" color="success" size="small" />
  );

  return (
    <Box>
      <Paper sx={filterCardSx}>
        <Typography variant="h6" gutterBottom>
          Relatório por Período
        </Typography>
        {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Data Início"
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              label="Data Fim"
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Local</InputLabel>
              <Select label="Local" value={localId} onChange={(e) => setLocalId(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {locais.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo</InputLabel>
              <Select label="Tipo" value={tipoId} onChange={(e) => setTipoId(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {tipos.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.tipo}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Equipamento</InputLabel>
              <Select label="Equipamento" value={equipamentoId} onChange={(e) => setEquipamentoId(e.target.value)}>
                <MenuItem value="">Todos</MenuItem>
                {equipamentos.map((item) => (
                  <MenuItem key={item.id} value={item.id}>{item.nome}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleBuscar}
              disabled={loading || !dataInicio || !dataFim}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Buscar'}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Manutenções Executadas</Typography>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => handlePdf('executadas')}
            disabled={loadingPdf !== null || !dataInicio || !dataFim}
          >
            {loadingPdf === 'executadas' ? <CircularProgress size={20} /> : 'Gerar PDF'}
          </Button>
        </Box>
        {executadas !== null && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell>Equipamento</TableCell>
                  <TableCell>Local</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Tipo Mnt.</TableCell>
                  <TableCell>Técnico</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {executadas.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">Nenhuma manutenção executada no período.</TableCell></TableRow>
                ) : executadas.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{formatDateOnly(item.data_execucao)}</TableCell>
                    <TableCell>{item.equipamento_nome || '-'}</TableCell>
                    <TableCell>{item.local_nome || '-'}</TableCell>
                    <TableCell>{item.tipo_nome || '-'}</TableCell>
                    <TableCell>{TIPO_MANUTENCAO_LABELS[item.tipo_manutencao] || item.tipo_manutencao}</TableCell>
                    <TableCell>{item.tecnico_nome || '-'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Manutenções a Executar</Typography>
          <Button
            variant="outlined"
            startIcon={<PictureAsPdfIcon />}
            onClick={() => handlePdf('a-executar')}
            disabled={loadingPdf !== null || !dataInicio || !dataFim}
          >
            {loadingPdf === 'a-executar' ? <CircularProgress size={20} /> : 'Gerar PDF'}
          </Button>
        </Box>
        {aExecutar !== null && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Equipamento</TableCell>
                  <TableCell>Local</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Última Execução</TableCell>
                  <TableCell>Próxima Manutenção</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aExecutar.length === 0 ? (
                  <TableRow><TableCell colSpan={6} align="center">Nenhuma manutenção a executar no período.</TableCell></TableRow>
                ) : aExecutar.map((item) => (
                  <TableRow key={item.equipamento_id}>
                    <TableCell>{item.equipamento_nome || '-'}</TableCell>
                    <TableCell>{item.local_nome || '-'}</TableCell>
                    <TableCell>{item.tipo_nome || '-'}</TableCell>
                    <TableCell>{formatDateOnly(item.ultima_data_execucao)}</TableCell>
                    <TableCell>{formatDateOnly(item.proxima_manutencao)}</TableCell>
                    <TableCell>{renderStatus(item.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}

export default ManutencaoRelatorio;
